import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CryptoPrice {
  symbol: string;
  usd_price: number;
  eur_price: number;
  gbp_price: number;
  price_change_24h: number;
  market_cap: number;
  volume_24h: number;
  last_updated: string;
}

export const useCryptoPrices = (symbols: string[]) => {
  const [prices, setPrices] = useState<Record<string, CryptoPrice>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchPrices = async () => {
    try {
      let { data, error } = await supabase
        .from('crypto_prices')
        .select('*')
        .in('symbol', symbols);

      if (error) throw error;

      // If no data, trigger a refresh from CoinGecko
      if (!data || data.length === 0) {
        console.log('No price data found, fetching from CoinGecko...');
        await supabase.functions.invoke('fetch-crypto-prices');
        // Retry fetching after refresh
        const refreshResult = await supabase
          .from('crypto_prices')
          .select('*')
          .in('symbol', symbols);
        
        if (refreshResult.data) {
          data = refreshResult.data;
        }
      }

      const pricesMap: Record<string, CryptoPrice> = {};
      data?.forEach((price) => {
        pricesMap[price.symbol] = {
          symbol: price.symbol,
          usd_price: price.usd_price ? parseFloat(price.usd_price.toString()) : 0,
          eur_price: price.eur_price ? parseFloat(price.eur_price.toString()) : 0,
          gbp_price: price.gbp_price ? parseFloat(price.gbp_price.toString()) : 0,
          price_change_24h: price.price_change_24h ? parseFloat(price.price_change_24h.toString()) : 0,
          market_cap: price.market_cap ? parseFloat(price.market_cap.toString()) : 0,
          volume_24h: price.volume_24h ? parseFloat(price.volume_24h.toString()) : 0,
          last_updated: price.last_updated,
        };
      });

      setPrices(pricesMap);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching crypto prices:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrices();

    // Refresh every 60 seconds
    const interval = setInterval(fetchPrices, 60000);

    return () => clearInterval(interval);
  }, [symbols.join(',')]);

  return { prices, isLoading, lastUpdate, refetch: fetchPrices };
};
